import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { Controller, useForm } from 'react-hook-form';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { WindowInformation } from '../../../services/window-information';
import MyAppText from '../../../components/MyAppText';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { licensePlateRecognitionService, LPRServiceError, PlateType } from '../../../services/central-api/license-plate-recognition';
import { useAuth } from '../../../context/Auth';
import PlateGroupsDropdown from '../../../components/dashboard/PlateGroupsDropdown';
import RegisterPlateInput from '../../../components/lpr/RegisterPlateInput';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedPlateId: number | undefined;
    onConfirm: () => void;
    onClose: () => void;
}

interface PlateForm {
    plates: string[];
    description: string;
    isActive: boolean;
    groupsIds: number[];
}

interface PlateFormInput {
    plate: string;
}

export default function RegisteredPlateModal({ isModalVisible, onConfirm, onClose, selectedPlateId, setModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const queryResult = useQuery({
        queryKey: ['registeredPlateModal', selectedPlateId],
        queryFn: () => licensePlateRecognitionService.getPlate(selectedPlateId!),
        enabled: !!selectedPlateId && !!authenticatedUser,
    });

    const isEnabled = !!selectedPlateId && !!authenticatedUser;
    const isFetchingInformation = isEnabled && queryResult.isLoading;

    const { data, refetch } = queryResult;

    const defaultEmptyValues: PlateForm = {
        plates: [],
        description: '',
        isActive: true,
        groupsIds: []
    };

    const form = useForm<PlateForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const closeModal = () => {
        form.reset(defaultEmptyValues);
        formInput.reset(defaultEmptyValuesInput);
        setModalVisible(false);
        onClose();
    };

    const defaultEmptyValuesInput: PlateFormInput = {
        // control variable used only for the frontend
        plate: '',
    };

    const formInput = useForm<PlateFormInput>({
        defaultValues: defaultEmptyValuesInput,
        mode: 'onChange',
    });

    function isPlateValid(plate: string): boolean {
        const platePattern = /^[A-Z]{3}-?(?:[0-9][0-9A-Z][0-9]{2}|49CC)$/;
        return platePattern.test(plate);
    }

    function IsPlatesValid() {
        const plates = form.watch('plates');
        const plate = formInput.watch('plate');

        return plates.length > 0 || (plate.trim() !== '' && isPlateValid(plate));
    }

    function IsFormValid() {
        if (form.watch('groupsIds').length === 0) {
            return false;
        }

        return IsPlatesValid();
    }

    useEffect(() => {
        if (isPlateValid(formInput.watch('plate'))) {
            form.trigger('plates');
        }
    }, [formInput.watch('plate')]);

    async function formSubmit() {
        try {
            setIsLoading(true);
            const data = form.getValues();

            if (selectedPlateId) {
                await licensePlateRecognitionService.updatePlate({
                    id: selectedPlateId,
                    description: data.description,
                    isActive: data.isActive,
                    groupsIds: data.groupsIds
                });
            } else {
                const currentPlate = formInput.watch('plate');
                const platesToSend = currentPlate.trim() !== '' && isPlateValid(currentPlate)
                    ? [...data.plates, currentPlate]
                    : data.plates;
                await licensePlateRecognitionService.createMultiplePlate({
                    plates: platesToSend,
                    description: data.description,
                    isActive: data.isActive,
                    type: PlateType.MONITORING,
                    groupsIds: data.groupsIds
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            if (err instanceof LPRServiceError) {
                if (err.message === 'PlateAlreadyExists') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('plateAlreadyRegistered')
                    });
                }
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }


    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        data.groupsIds = data.groups?.map(g => g.id);
        form.reset({
            description: data.description,
            groupsIds: data.groupsIds,
            isActive: data.isActive,
            plates: []
        });
        formInput.reset({
            plate: data.plate
        });

    }, [selectedPlateId, data, authenticatedUser]);


    useEffect(() => {
        if (!isModalVisible || !authenticatedUser) {
            return;
        }

        if (selectedPlateId) {
            refetch();
        } else {
            form.reset(defaultEmptyValues);
            formInput.reset(defaultEmptyValuesInput);
        }
    }, [selectedPlateId, authenticatedUser, isModalVisible]);

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    {isFetchingInformation
                        ? <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        </View>
                        : <>
                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.header}>{translate('monitoringVehicleLicensePlatesRegister')}</MyAppText>
                                <MyAppText style={styles.subheader}>{translate('fillFieldsBelowToFinishPlateRegister')}</MyAppText>
                            </View>

                            <Controller
                                control={form.control}
                                name='groupsIds'
                                rules={{
                                    required: translate('required_field'),
                                    min: 1,
                                }}
                                render={({ field }) => (
                                    <PlateGroupsDropdown
                                        value={field.value}
                                        setValue={(val) => {
                                            const newValue = typeof val === 'function'
                                                ? val(field.value || [])
                                                : val;
                                            field.onChange(newValue);
                                        }}
                                        zIndex={2}
                                        required
                                    />
                                )}
                            />
                            <RegisterPlateInput form={form} fieldName='plates' />
                            <FormInput
                                label={translate('description')}
                                required={false}
                                form={form}
                                fieldName='description'
                                multiline
                                numberOfLines={3}
                                labelStyle={styles.inputLabelStyle}
                            />
                            <FormActions
                                viewStyle={{ flex: 1, justifyContent: 'flex-end' }}
                                buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                                buttonContentContainerStyle={{ minWidth: 100 }}
                                onSubmit={formSubmit}
                                onClose={closeModal}
                                disabled={!IsFormValid()}
                                isLoading={isLoading}
                            />
                        </>
                    }
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 635,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        // height: 500,
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
}));
